import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, Promise<Element | null>>} */
  #pendingContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
    this.addEventListener('pointerenter', this.#prefetchProductGrid, { passive: true });
    this.addEventListener('focusin', this.#prefetchProductGrid);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
    this.removeEventListener('pointerenter', this.#prefetchProductGrid);
    this.removeEventListener('focusin', this.#prefetchProductGrid);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
    this.#pendingContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Fetches and caches the product grid. Pending requests are shared so a hover
   * prefetch and a click never download the same product page twice.
   * @param {string} currentUrl
   * @returns {Promise<Element | null>}
   */
  #getProductGrid = async (currentUrl) => {
    if (!currentUrl) return null;

    const cached = this.#cachedContent.get(currentUrl);
    if (cached) return cached;

    const pending = this.#pendingContent.get(currentUrl);
    if (pending) return pending;

    const request = (async () => {
      const html = await this.fetchProductPage(currentUrl);
      if (!html) return null;

      const gridElement = html.querySelector('[data-product-grid-content]');
      if (!gridElement) return null;

      const productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
      this.#cachedContent.set(currentUrl, productGrid);
      return productGrid;
    })();

    this.#pendingContent.set(currentUrl, request);

    try {
      return await request;
    } finally {
      this.#pendingContent.delete(currentUrl);
    }
  };

  #prefetchProductGrid = () => {
    if (this.dataset.usesSellingPlans === 'true') return;
    if (this.dataset.quickAddButton !== 'choose') return;

    const currentUrl = this.productPageUrl;
    if (!currentUrl || this.#cachedContent.has(currentUrl) || this.#pendingContent.has(currentUrl)) return;

    this.#getProductGrid(currentUrl).catch(() => {});
  };

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    const dialogComponent = document.getElementById('quick-add-dialog');
    const hasCachedContent = this.#cachedContent.has(currentUrl);

    if (dialogComponent instanceof QuickAddDialog && !hasCachedContent) {
      dialogComponent.setAttribute('data-loading', 'true');
      this.#openQuickAddModal();
    }

    try {
      const productGrid = await this.#getProductGrid(currentUrl);

      if (productGrid) {
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        await this.updateQuickAddModal(freshContent);
        this.#updateVariantPicker(productGrid);
      }

      if (dialogComponent instanceof QuickAddDialog) {
        dialogComponent.removeAttribute('data-loading');
      }

      if (hasCachedContent) {
        this.#openQuickAddModal();
      }
    } catch (error) {
      if (dialogComponent instanceof QuickAddDialog) {
        dialogComponent.removeAttribute('data-loading');
      }
      throw error;
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    const quickAddSummary = this.#extractQuickAddSummary(productGrid);

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#injectProductSummary(modalContent, quickAddSummary);
    this.#syncVariantSelection(modalContent);
    this.#prefetchQuickAddVariants(modalContent);
  }

  /**
   * Extract only the purchasing essentials from the PDP description:
   * What's Included (or an Included line from Product Details) and Size & Details.
   * @param {Element} productGrid
   * @returns {HTMLElement | null}
   */
  #extractQuickAddSummary(productGrid) {
    const candidates = Array.from(productGrid.querySelectorAll('.product-details .text-block'));
    const source = candidates.find((element) => {
      const text = element.textContent || '';
      return /Size\s*&\s*Details/i.test(text) || /What.?s\s+Included/i.test(text) || /Product\s+Details/i.test(text);
    });

    if (!(source instanceof HTMLElement)) return null;

    const summary = document.createElement('div');
    summary.className = 'mahjwell-quick-add-summary';

    let sectionRoot = source;
    const directHasHeadings = Array.from(source.children).some((child) => /^H[1-6]$/.test(child.tagName));
    if (!directHasHeadings) {
      const nestedRoot = Array.from(source.children).find((child) =>
        Array.from(child.children || []).some((grandchild) => /^H[1-6]$/.test(grandchild.tagName))
      );
      if (nestedRoot instanceof HTMLElement) sectionRoot = nestedRoot;
    }

    const children = Array.from(sectionRoot.children);
    const isHeading = (element) => /^H[1-6]$/.test(element.tagName);
    const headingText = (element) => (element.textContent || '').trim();

    const findSection = (pattern) => {
      const startIndex = children.findIndex((child) => isHeading(child) && pattern.test(headingText(child)));
      if (startIndex < 0) return [];

      const section = [];
      for (let index = startIndex + 1; index < children.length; index += 1) {
        const child = children[index];
        if (isHeading(child)) break;
        section.push(child);
      }
      return section;
    };

    const explicitIncluded = findSection(/^What.?s\s+Included$/i);
    if (explicitIncluded.length) {
      const heading = document.createElement('h3');
      heading.textContent = "What's Included";
      summary.appendChild(heading);
      explicitIncluded.forEach((node) => summary.appendChild(node.cloneNode(true)));
    } else {
      const productDetails = findSection(/^Product\s+Details$/i);
      const includedItems = [];

      productDetails.forEach((node) => {
        const items = node.matches?.('li') ? [node] : Array.from(node.querySelectorAll?.('li') || []);
        items.forEach((item) => {
          if (/^Included\s*:/i.test((item.textContent || '').trim())) {
            includedItems.push(item);
          }
        });
      });

      if (includedItems.length) {
        const heading = document.createElement('h3');
        heading.textContent = "What's Included";
        const list = document.createElement('ul');
        includedItems.forEach((item) => list.appendChild(item.cloneNode(true)));
        summary.append(heading, list);
      }
    }

    const sizeDetails = findSection(/^Size\s*&\s*Details$/i);
    if (sizeDetails.length) {
      const heading = document.createElement('h3');
      heading.textContent = 'Size & Details';
      summary.appendChild(heading);
      sizeDetails.forEach((node) => summary.appendChild(node.cloneNode(true)));
    }

    return summary.children.length ? summary : null;
  }

  /**
   * Inserts the compact summary above the sticky purchase buttons.
   * @param {Element} modalContent
   * @param {HTMLElement | null} summary
   */
  #injectProductSummary(modalContent, summary) {
    modalContent.querySelector('.mahjwell-quick-add-summary')?.remove();
    if (!summary) return;

    const details = modalContent.querySelector('.product-details');
    const buyButtons = details?.querySelector('.buy-buttons-block');

    if (buyButtons) {
      buyButtons.before(summary);
      return;
    }

    if (details) {
      details.append(summary);
      return;
    }

    const productForm = modalContent.querySelector('product-form-component');
    if (productForm) {
      productForm.before(summary);
    } else {
      modalContent.append(summary);
    }
  }

  /**
   * Prefetches Quick Add variant section HTML and the first image for each option.
   * VariantPicker consumes completed responses from the shared in-memory cache.
   * @param {Element} modalContent
   */
  #prefetchQuickAddVariants(modalContent) {
    const picker = modalContent.querySelector('variant-picker');
    if (!(picker instanceof VariantPicker)) return;

    if (!(window.__mahjwellQuickAddVariantCache instanceof Map)) {
      window.__mahjwellQuickAddVariantCache = new Map();
    }

    const cache = window.__mahjwellQuickAddVariantCache;
    const currentValues = picker.selectedOptionsValues;
    const optionInputs = Array.from(picker.querySelectorAll('input[data-option-value-id]'));
    const urls = new Set();

    for (const input of optionInputs) {
      if (!(input instanceof HTMLInputElement)) continue;

      const optionValueId = input.dataset.optionValueId;
      if (!optionValueId) continue;

      const fieldsetIndex = Number.parseInt(input.dataset.fieldsetIndex || '0');
      const intendedValues = [...currentValues];

      if (Number.isFinite(fieldsetIndex)) {
        intendedValues[fieldsetIndex] = optionValueId;
      }

      const requestUrl = picker.buildRequestUrl(input, 'product-card', intendedValues);
      if (requestUrl && !cache.has(requestUrl)) urls.add(requestUrl);
    }

    const prefetch = (requestUrl) => {
      if (cache.has(requestUrl)) return;

      fetch(requestUrl, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`Variant prefetch failed: ${response.status}`);
          return response.text();
        })
        .then((responseText) => {
          cache.set(requestUrl, responseText);

          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const firstImage = html.querySelector('media-gallery slideshow-slide img');

          if (firstImage instanceof HTMLImageElement) {
            const preload = new Image();
            preload.decoding = 'async';
            preload.src = firstImage.currentSrc || firstImage.src;
          }
        })
        .catch(() => {
          // Prefetch is opportunistic; normal VariantPicker fetching remains the fallback.
        });
    };

    const runPrefetch = () => {
      let delay = 0;
      for (const requestUrl of urls) {
        window.setTimeout(() => prefetch(requestUrl), delay);
        delay += 45;
      }
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(runPrefetch, { timeout: 250 });
    } else {
      window.setTimeout(runPrefetch, 40);
    }
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
